import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { AlertTriangle, RefreshCw, Trash2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAdminToken = () =>
  sessionStorage.getItem("access_token") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("admin_token") ||
  "";

/**
 * EbayDuplicatesWidget — surfaces eBay orders that were saved twice due to
 * the 2026-02 multi-replica race condition. Historical fix; new sync flow
 * is protected via atomic upsert. One-click cleanup keeps the OLDEST
 * document of each duplicate group.
 */
export const EbayDuplicatesWidget = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const r = await axios.get(`${API}/ebay/duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(r.data?.groups || []);
    } catch (e) {
      console.error("[ebay-duplicates] fetch failed", e?.response?.status);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleCleanup = async () => {
    const totalToDelete = groups.reduce((acc, g) => acc + (g.count - 1), 0);
    if (!window.confirm(
      `Eliminare ${totalToDelete} ordini duplicati (${groups.length} gruppi)?\n\n` +
      `Verrà mantenuto l'ordine più vecchio di ogni gruppo. L'operazione NON è reversibile.`
    )) return;
    setBusy(true);
    setMessage(null);
    try {
      const token = getAdminToken();
      const r = await axios.post(
        `${API}/ebay/duplicates/cleanup?dry_run=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const deleted = r.data?.deleted || 0;
      setMessage({ type: "success", text: `✅ ${deleted} ordini duplicati eliminati` });
      await fetchGroups();
    } catch (e) {
      setMessage({ type: "error", text: `Errore: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(false);
    }
  };

  const totalDuplicates = groups.reduce((acc, g) => acc + (g.count - 1), 0);

  // Hide widget entirely when there are no duplicates → no visual noise on a healthy dashboard.
  if (!loading && groups.length === 0 && !message) {
    return null;
  }

  return (
    <Card className="bg-gray-800 border-amber-700/60" data-testid="ebay-duplicates-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Ordini eBay Duplicati
          {totalDuplicates > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-500 text-black" data-testid="ebay-duplicates-count">
              {totalDuplicates}
            </span>
          )}
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchGroups}
          disabled={loading}
          className="text-gray-300 border-gray-600 hover:bg-gray-700"
          data-testid="ebay-duplicates-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </CardHeader>

      <CardContent>
        {loading && groups.length === 0 ? (
          <p className="text-gray-400 text-sm">Caricamento…</p>
        ) : groups.length === 0 ? (
          <div className="flex items-center gap-2 text-green-400 text-sm" data-testid="ebay-duplicates-empty">
            <Check className="w-4 h-4" />
            Nessun duplicato rilevato. Ottimo!
          </div>
        ) : (
          <>
            <p className="text-sm text-amber-200 mb-3">
              Rilevati <strong>{totalDuplicates}</strong> ordini duplicati in <strong>{groups.length}</strong> gruppi.
              Verrà mantenuto l&apos;ordine più vecchio di ciascun gruppo.
            </p>
            <div className="max-h-48 overflow-y-auto rounded border border-gray-700 mb-3">
              <table className="w-full text-xs text-gray-200">
                <thead className="bg-gray-900 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">eBay Order ID</th>
                    <th className="px-3 py-2 font-medium text-right">Copie</th>
                    <th className="px-3 py-2 font-medium text-right">Totale (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.ebay_order_id} className="border-t border-gray-700">
                      <td className="px-3 py-1.5 font-mono">{g.ebay_order_id}</td>
                      <td className="px-3 py-1.5 text-right">{g.count}</td>
                      <td className="px-3 py-1.5 text-right">
                        {g.orders?.[0]?.total_amount?.toFixed?.(2) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={handleCleanup}
              disabled={busy}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="ebay-duplicates-cleanup"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {busy ? "Pulizia in corso…" : `Elimina ${totalDuplicates} duplicati`}
            </Button>
          </>
        )}

        {message && (
          <div
            className={`mt-3 p-2 rounded text-sm ${
              message.type === "success" ? "bg-green-900/40 text-green-200" : "bg-red-900/40 text-red-200"
            }`}
            data-testid="ebay-duplicates-message"
          >
            {message.text}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
